import React, { useMemo, useCallback, useState } from "react";
import { FormControl, InputLabel, Paper, TextField, Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import Autocomplete from "@mui/material/Autocomplete";
import { useTheme } from "@mui/material/styles";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { shouldBlockUnsafeSearchInput } from "@/helpers/sanitizeHelper";
import { primaryBadgeSx } from "../styles/badgeSx";

type SelectOption = {
  label?: string;
  value?: any;
  [key: string]: any;
};

type SelectProps = {
  value?: any;
  onChange?: (_value: any) => void;
  [key: string]: any;
};

type MUICustomAutocompleteSelectProps = {
  label?: string;
  name?: string;
  selectProps?: SelectProps;
  labelProps?: Record<string, any>;
  options?: SelectOption[];
  wrapperProps?: Record<string, any>;
  all?: boolean;
  allText?: string;
  isOutlined?: boolean;
  hideLabel?: boolean;
  placeholder?: string;
  multiple?: boolean;
  disableClearable?: boolean;
  renderOption?: ((_option: SelectOption) => React.ReactNode) | null;
  onInputChange?: (..._args: any[]) => void;
  [key: string]: any;
};

const CustomAutocomplete = styled(Autocomplete as any)(({ theme }) => ({
  zIndex: "1 !important",
  borderRadius: "var(--radius-control)",
  border: "1px solid var(--neutral-500)",
  boxSizing: "border-box",
  display: "flex",
  minHeight: "40px",
  transition: theme.transitions.create(["border-color", "box-shadow"]),
  "&:hover": {
    borderRadius: "var(--radius-control)",
    borderColor: "var(--neutral-600) !important",
  },
  // The outline paints above the inner input, so all four sides stay 2px.
  "&:focus-within": {
    borderRadius: "var(--radius-control)",
    borderColor: "var(--primary-base) !important",
    outline: "1px solid var(--primary-base)",
    outlineOffset: "-2px",
  },
  "& .MuiAutocomplete-input": {
    fontSize: ".8rem",
  },
  "& .MuiAutocomplete-popupIndicator": {
    color: "inherit",
  },
  "& .MuiAutocomplete-popper": {
    boxShadow: "var(--shadow-floating)",
  },
  "& .MuiAutocomplete-paper": {
    boxShadow: "none",
  },
  "& .MuiAutocomplete-noOptions": {
    fontSize: ".8rem",
    fontWeight: 600,
    padding: "10px 16px 5px",
  },
  "& .MuiAutocomplete-tag": {
    height: "20px !important",
    fontSize: ".7rem !important",
    fontWeight: "700 !important",
    minWidth: "60px !important",
    margin: "3px !important",
    "& .MuiChip-label": {
      fontSize: ".7rem !important",
      fontWeight: "600 !important",
    },
  },
}));

const StyledPaper = styled(Paper)(() => ({
  backgroundColor: "transparent",
  boxShadow: "none",
}));

const StyledInputLabel = styled(InputLabel)(() => ({
  fontWeight: 600,
  color: "var(--neutral-700)",
  fontSize: ".92rem",
  marginTop: -11,
  marginLeft: -11,
}));

export function MUICustomAutocompleteSelect({
  label = "",
  name = "select",
  selectProps = {},
  labelProps = {},
  options = [],
  wrapperProps = {},
  all,
  allText = "All",
  isOutlined = false,
  hideLabel = false,
  placeholder = "Search..",
  multiple = false,
  disableClearable = true,
  renderOption = null, // Custom render function for options
  ...rest
}: MUICustomAutocompleteSelectProps) {
  const { onInputChange: externalOnInputChange, ...autocompleteProps } = rest;
  const [inputValue, setInputValue] = useState("");
  const theme = useTheme();

  const dropdownOptions = useMemo(
    () => (all ? [{ label: allText, value: "" }, ...options] : options),
    [options, all, allText]
  );

  const selectedValue = useMemo(() => {
    if (multiple) {
      // For multi-select, value should be an array
      if (
        !selectProps.value ||
        !Array.isArray(selectProps.value) ||
        selectProps.value.length === 0
      ) {
        return [];
      }
      return selectProps.value.map(
        (val: any) =>
          dropdownOptions.find((o) => o.value === val) || {
            label: val,
            value: val,
          }
      );
    } else {
      // For single select
      const v = dropdownOptions.find((o) => o.value === selectProps.value);
      return v || (all ? { label: allText, value: "" } : null);
    }
  }, [selectProps.value, dropdownOptions, all, allText, multiple]);

  // Sync inputValue with selectedValue to handle external resets
  React.useEffect(() => {
    if (multiple) {
      setInputValue("");
    } else {
      setInputValue(
        Array.isArray(selectedValue) ? "" : selectedValue?.label || ""
      );
    }
  }, [selectedValue, multiple, selectProps.value]);

  const handleChange = useCallback(
    (event: React.SyntheticEvent, newValue: any) => {
      if (multiple) {
        // For multi-select, newValue is an array
        const values = newValue ? newValue.map((item) => item.value) : [];
        selectProps.onChange?.(values);
      } else {
        // For single select
        selectProps.onChange?.(newValue?.value);
      }
    },
    [selectProps, multiple]
  );

  const handleInputChange = useCallback(
    (event: React.SyntheticEvent, newInputValue: string, reason: string) => {
      if (reason === "input" && shouldBlockUnsafeSearchInput(newInputValue)) {
        return;
      }

      setInputValue(newInputValue);
      externalOnInputChange?.(event, newInputValue, reason);
    },
    [externalOnInputChange]
  );

  const getStyles = useCallback(
    () => ({
      autocomplete: {
        background: rest.disabled
          ? (theme.vars || theme)?.palette?.neutral?.[200] ||
            "var(--neutral-200)"
          : isOutlined
            ? "transparent"
            : (theme.vars || theme)?.palette?.background?.paper,
        color: rest.disabled
          ? (theme.vars || theme)?.palette?.neutral?.[500] ||
            "var(--neutral-500)"
          : isOutlined
            ? (theme.vars || theme)?.palette?.primary?.main
            : (theme.vars || theme)?.palette?.text?.primary,
        border: `1px solid ${
          rest.disabled
            ? (theme.vars || theme)?.palette?.action?.disabledBackground
            : "var(--neutral-500)"
        }`,
      },
      input: {
        alignItems: "center",
        color: "inherit",
        border: "none",
        minHeight: "38px",
        paddingLeft: "10px",
      },
      popupIndicator: {
        color: rest.disabled
          ? (theme.vars || theme)?.palette?.neutral?.[500] ||
            "var(--neutral-500)"
          : isOutlined
            ? (theme.vars || theme)?.palette?.primary?.main
            : (theme.vars || theme)?.palette?.text?.secondary,
      },
    }),
    [rest.disabled, isOutlined, theme]
  );

  const styles = getStyles();

  return (
    <StyledPaper elevation={0} className="px-2" {...wrapperProps}>
      <FormControl fullWidth margin="dense" className="my-0 py-3 py-lg-0">
        {!hideLabel && (
          <StyledInputLabel
            shrink
            id={`${name}-label`}
            {...labelProps}
            // margin="dense"
            disableAnimation
            required={rest?.required}
            className="py-3 py-lg-0"
          >
            {label}
          </StyledInputLabel>
        )}
        <CustomAutocomplete
          {...autocompleteProps}
          style={styles.autocomplete}
          disableClearable={disableClearable}
          clearIcon={<HighlightOffIcon style={styles.popupIndicator} />}
          openOnFocus
          id={name}
          name={name}
          fullWidth
          multiple={multiple}
          value={selectedValue}
          onChange={handleChange}
          inputValue={inputValue}
          onInputChange={handleInputChange}
          options={dropdownOptions}
          getOptionLabel={(option: SelectOption) => option?.label || ""}
          isOptionEqualToValue={(option: SelectOption, value: SelectOption) =>
            option?.value === value?.value
          }
          slotProps={{
            popper: {
              sx: {
                marginTop: "5px !important",
              },
            },
            chip: { sx: primaryBadgeSx },
          }}
          renderOption={(props: any, option: SelectOption) => {
            // Use custom render function if provided
            if (renderOption) {
              return <li {...props}>{renderOption(option)}</li>;
            }

            // Default rendering
            return (
              <li {...props}>
                <Box className="d-flex align-items-center">
                  <span>{option?.label || ""}</span>
                </Box>
              </li>
            );
          }}
          ListboxProps={{
            sx: {
              "& .MuiAutocomplete-option": {
                fontSize: ".8rem",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  fontWeight: 600,
                  backgroundColor: (theme.vars || theme).palette.action.hover,
                },
                "&.Mui-focused": {
                  backgroundColor: (theme.vars || theme).palette.action.hover,
                },
                "&.Mui-selected": {
                  backgroundColor: (theme.vars || theme).palette.action
                    .selected,
                  color: (theme.vars || theme)?.palette?.primary?.main,
                  fontWeight: 600,
                },
              },
            },
          }}
          renderInput={(params: any) => (
            <TextField
              {...params}
              variant="standard"
              placeholder={placeholder}
              slotProps={{
                input: {
                  ...params.InputProps,
                  disableUnderline: true,
                  style: styles.input,
                },
              }}
            />
          )}
          popupIcon={<ArrowDropDownIcon style={styles.popupIndicator} />}
        />
      </FormControl>
    </StyledPaper>
  );
}
